import React, { useEffect, useRef } from "react";
import BrandSelectorHeader from "./BrandSelectorHeader";
import BrandSelectorContent from "./BrandSelectorContent";
import BrandSelectorFooter from "./BrandSelectorFooter";
import { BrandSelectorPopupProps } from "../../types";

const BrandSelectorPopup: React.FC<BrandSelectorPopupProps> = ({
	state,
	selectedBrandNames,
	brandOptions,
	brandDataLoading,
	fieldKey,
	record,
	i,
	updateTreeSelectState,
	generateCoreBrandData,
	handleCoreBrandInputChange,
	mergeCompleteSubbrandsToParent,
	findBrandByName,
}) => {
	// Read primitives off state rather than depending on the object, whose identity
	// changes on every update and would re-fire the effect below constantly.
	const isOpen = state.isOpen;
	const customName = state.customName;
	const tempSelectedBrandsLength = state.tempSelectedBrands?.length || 0;

	// Keep refs to the latest function props so the effect doesn't need them as deps
	// (prevents the effect from re-running when inline lambdas are recreated each render)
	const findBrandByNameRef = useRef(findBrandByName);
	const mergeCompleteSubbrandsToParentRef = useRef(mergeCompleteSubbrandsToParent);
	findBrandByNameRef.current = findBrandByName;
	mergeCompleteSubbrandsToParentRef.current = mergeCompleteSubbrandsToParent;

	// A customName with no tempSelectedBrands means an earlier match attempt ran before
	// brandOptions had loaded. Retry once they arrive, regardless of whether the dropdown
	// is open, so the selection is already correct the next time it opens.
	useEffect(() => {
		const hasValidBrandOptions =
			brandOptions &&
			brandOptions.length > 0 &&
			brandOptions[0]?.value !== "core_brand_1"; // the placeholder option

		const hasCustomName = customName && customName.trim() !== "";
		const hasNoSelectedBrands = tempSelectedBrandsLength === 0;

		console.log(`[BrandSelectorPopup] useEffect check for ${fieldKey}:`, {
			isOpen,
			brandDataLoading,
			hasValidBrandOptions,
			hasCustomName,
			hasNoSelectedBrands,
			brandOptionsLength: brandOptions?.length,
			customName,
			tempSelectedBrandsLength,
		});

		if (
			!brandDataLoading &&
			hasValidBrandOptions &&
			hasCustomName &&
			hasNoSelectedBrands
		) {
			console.log(
				`[BrandSelectorPopup] 🔄 Re-matching customName "${customName}" with brandOptions for ${fieldKey}`,
				{
					brandOptionsLength: brandOptions.length,
					customName,
					tempSelectedBrands: state.tempSelectedBrands,
				}
			);

			const matchResult = findBrandByNameRef.current(customName);

			if (matchResult) {
				console.log(
					`[BrandSelectorPopup] ✅ Matched "${customName}" to brand:`,
					matchResult
				);
				updateTreeSelectState(fieldKey, {
					tempSelectedBrands: [matchResult.value],
					tempCustomName: customName,
				});
			} else {
				// customName isn't a brand label, so fall back to the row's saved payload.
				const currentBrandValue =
					record[`coreBrand${i + 1}` as keyof typeof record];
				if (currentBrandValue && currentBrandValue !== "-") {
					try {
						const parsedData = JSON.parse(currentBrandValue);
						if (parsedData.brandData && parsedData.brandData.content) {
							const savedBrands: string[] = [];

							parsedData.brandData.content.forEach((item: any) => {
								const itemMatchResult = findBrandByNameRef.current(item.brand, item.subbrand);
								if (itemMatchResult) {
									savedBrands.push(itemMatchResult.value);
								}
							});

							if (savedBrands.length > 0) {
								const mergedBrands =
									mergeCompleteSubbrandsToParentRef.current(savedBrands);
								console.log(
									`[BrandSelectorPopup] ✅ Restored brands from saved data:`,
									mergedBrands
								);
								updateTreeSelectState(fieldKey, {
									tempSelectedBrands: mergedBrands,
									tempCustomName: customName,
								});
							} else {
								console.warn(
									`[BrandSelectorPopup] ⚠️ No brands matched from saved data for ${fieldKey}`
								);
							}
						}
					} catch (error) {
						console.warn(
							`[BrandSelectorPopup] ⚠️ Failed to parse saved data for ${fieldKey}:`,
							error
						);
					}
				} else {
					console.warn(
						`[BrandSelectorPopup] ⚠️ No saved data found for ${fieldKey}, customName: "${customName}"`
					);
				}
			}
		}
	// Only re-run when brandOptions actually loads/changes or brandDataLoading toggles.
	// Do NOT include tempSelectedBrandsLength, findBrandByName, mergeCompleteSubbrandsToParent,
	// or updateTreeSelectState — those either change on every render (inline lambdas) or
	// when user clears selection, which would cause the effect to re-add just-removed brands.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		brandOptions?.length,
		brandOptions?.[0]?.value,
		brandDataLoading,
		customName,
		fieldKey,
		record,
		i,
	]);

	return (
		<div className="w-[400px] bg-white rounded-3xl shadow-[0px_4px_12px_0px_rgba(166,166,166,0.25)] border border-gray-200 overflow-hidden">
			{/* Header Section */}
			<BrandSelectorHeader
				state={state}
				selectedBrandNames={selectedBrandNames}
				brandOptions={brandOptions}
				fieldKey={fieldKey}
				updateTreeSelectState={updateTreeSelectState}
			/>

			{/* Content Section */}
			<BrandSelectorContent
				brandDataLoading={brandDataLoading}
				brandOptions={brandOptions}
				state={state}
				fieldKey={fieldKey}
				updateTreeSelectState={updateTreeSelectState}
				mergeCompleteSubbrandsToParent={mergeCompleteSubbrandsToParent}
			/>

			{/* Footer Section */}
			<BrandSelectorFooter
				state={state}
				fieldKey={fieldKey}
				record={record}
				i={i}
				updateTreeSelectState={updateTreeSelectState}
				generateCoreBrandData={generateCoreBrandData}
				handleCoreBrandInputChange={handleCoreBrandInputChange}
			/>
		</div>
	);
};

export default BrandSelectorPopup;
